import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Tooltip } from 'antd';
import { Icon, Button, DropdownButton } from 'ui';
import { FormattedMessage } from 'react-intl';

const ThemeHeader = styled.div`
  border-radius: 5px 5px 0 0;
  width: 100%;
  text-transform: uppercase;
  font-size: 14px;
  display: flex;
  align-items: center;
  color: rgb(255, 255, 255);
  font-weight: 700;
  background-color: rgb(0, 152, 204) !important;
  top: 0px;
  padding: 0.5rem 20px !important;
  ${p => p.height && `height: ${p.height};`}
  ${p => !p.inlineTable && 'margin-bottom: 12px;'}
`;

const Header = styled.tr`
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
  ${p => p.fullWidth && 'padding-left: 20px'};
  background-color: ${p => p.light ? '#fff' : 'rgb(209,214,218)'};
  border-bottom: 1px solid #B6BBBF;
  border-top: ${p => p.light ? 'none' : '1px solid #B6BBBF'};
`;

const HeaderColumn = styled.th`
  flex: ${p => p.width ? `0 0 ${p.width}` : '1'};
  height: 30px;
  margin-right: 20px;
  display: flex;
  align-items: center;
`;

const HeaderLabel = styled.div`
  font-size: 14px;
  font-weight: ${p => p.light ? '600' : '400'};
  display: flex;
  flex-flow: row nowrap;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const StyledEditButtons = styled.div`
  button{
    margin-left: 15px;
    padding: 0 18px;
    height: 20px !important;;
    font-weight: 900;
    color: rgb(0, 152, 204);
    background-color: white;
  }

  .ant-dropdown-menu,
  .ant-dropdown-menu > li,
  .ant-dropdown-menu > li:hover,
  button:hover,
  button:active,
  button:focus {
    background-color: white !important;
    color: rgb(0, 152, 204) !important;
  }
`;

const Icons = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: ${p => p.gap || 20}px;
`;

const StyledIcon = styled(Icon)`
  color: ${p => p.disabled ? 'rgba(0, 0, 0, 0.3)' : p.color};
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  ${p => p.fontSize && `font-size: ${p.fontSize}px;`}
`;

const SubHeader = ({
  columns = [],
  data = [],
  light = true,
  fullWidth = true,
}) =>
  columns?.length > 1 && data?.length > 0 &&
  <Header fullWidth={fullWidth} light={light} >
    {columns?.map(c =>
      <HeaderColumn width={c.width} key={c.id}>
        <HeaderLabel light={light}>{c.displayName}</HeaderLabel>
      </HeaderColumn>,
    )}
  </Header>;

SubHeader.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  light: PropTypes.bool,
  fullWidth: PropTypes.bool,
};

const showIconsButton = (iconKey, action, disabled, light, fontSize, color) => <div>
  <StyledIcon
    type={iconKey}
    light={light}
    fontSize={fontSize}
    color={color}
    onClick={() => {
      if (!disabled) { action(); }
    }}
    disabled={disabled}
  />
</div>;


const RowIconAction = ({
  title = '',
  tooltipKey,
  iconKey,
  light = false,
  action,
  color = '#0098cc',
  disabled = false,
  fontSize,
}) =>
  tooltipKey ?
    <Tooltip
      title={title}
      key={tooltipKey}
      mouseEnterDelay={0.5}
      getPopupContainer={() => document.body}
    >
      {showIconsButton(iconKey, action, disabled, light, fontSize, color)}
    </Tooltip >
    :
    showIconsButton(iconKey, action, disabled, light, fontSize, color);

RowIconAction.propTypes = {
  title: PropTypes.string,
  tooltipKey: PropTypes.string,
  iconKey: PropTypes.string.isRequired,
  light: PropTypes.bool,
  action: PropTypes.func.isRequired,
  color: PropTypes.string,
  disabled: PropTypes.bool,
  fontSize: PropTypes.number,
};

const EditButtons = ({
  hidden = true,
  restoreOriginalValues,
  editMode = false,
  setEditMode,
  saveNewValues,
}) => !hidden && <StyledEditButtons>
  {
    editMode ?
      <DropdownButton
        text={<FormattedMessage id='common.SAVE' />}
        onClick={() => saveNewValues()}
        subItems={
          [
            {
              text: <FormattedMessage id='common.CANCEL' />,
              onClick: () => restoreOriginalValues(),
            },
          ]}
      />
      :
      <Button onClick={() => setEditMode(true)} >
        <FormattedMessage id='common.EDIT' />
      </Button>
  }
</StyledEditButtons>;

EditButtons.propTypes = {
  hidden: PropTypes.bool,
  restoreOriginalValues: PropTypes.func,
  editMode: PropTypes.bool,
  setEditMode: PropTypes.func,
  saveNewValues: PropTypes.func,
};

export {
  RowIconAction,
  ThemeHeader,
  Icons,
  EditButtons,
  Header,
  HeaderColumn,
  HeaderLabel,
  SubHeader,
};
